import { type ReactNode, useState } from "react";
import { queries } from "../../shared/queries";
import { ATTACHMENTS_VOLUME } from "../../shared/volumes/storagePath";
import { useQuery } from "@rocicorp/zero/react";
import { useVolumeDirectory } from "../hooks/useVolumeDirectory";
import { spaceVolumePath, volumeFilePath } from "../lib/apiPaths";
import { FilePreviewPanel } from "./AttachmentHoverPreview";
import { EmptyState } from "./EmptyState";
import { DraftStorageEmptyState } from "./FileBrowser/DraftStorageEmptyState";
import { ResetBranchDataButton } from "./FileBrowser/ResetBranchDataButton";
import { FileList, FileListRow } from "./FileList";
import type { FilePreviewTarget } from "./FileRender";
import { PanelTransition } from "./PanelTransition";
import { WavyLine } from "./WavyLine";

type Directory = {
  type: "directory";
  title: string;
  pathBase: string;
  relativePath: string;
};

type Location = Directory | { type: "file"; file: FilePreviewTarget };

type Props = {
  spaceId: string;
  branch: { id: string; name: string; workflowId: string };
  canWrite: boolean;
  volumeName?: string;
};

function locationTitle(location: Location | undefined) {
  return location?.type === "file"
    ? location.file.fileName
    : (location?.title ?? "Files");
}

export function WorkflowFilesPanel(props: Props) {
  const [locations, setLocations] = useState<Location[]>([]);
  const current = locations.at(-1);
  const viewKey =
    current?.type === "file"
      ? current.file.url
      : current
        ? `${current.pathBase}/${current.relativePath}`
        : "files";
  const backLabel = `Back to ${locations.length > 1 ? locationTitle(locations.at(-2)) : (props.volumeName ?? "Files")}`;
  const back = () => setLocations((previous) => previous.slice(0, -1));
  const open = (location: Location) =>
    setLocations((previous) => [...previous, location]);

  return (
    <PanelTransition viewKey={viewKey} depth={locations.length}>
      {current?.type === "file" ? (
        <FilePreviewPanel
          key={viewKey}
          embedded
          interactive
          {...current.file}
          onBack={back}
          backLabel={backLabel}
          onOpenFile={(file) => {
            setLocations((previous) =>
              previous === locations
                ? [...previous, { type: "file", file }]
                : previous
            );
          }}
        />
      ) : current ? (
        <DirectoryFiles
          key={viewKey}
          directory={current}
          onOpen={open}
          onBack={back}
          backLabel={backLabel}
        />
      ) : (
        <WorkflowFolders {...props} onOpen={open} />
      )}
    </PanelTransition>
  );
}

function WorkflowFolders({
  spaceId,
  branch,
  canWrite,
  volumeName,
  onOpen,
}: Props & { onOpen: (location: Location) => void }) {
  const [spaceVolumes, result] = useQuery(queries.spaceVolumes({ spaceId }));
  const volumes = spaceVolumes.filter(
    (volume) => volume.branchId === branch.id
  );
  const isDraft = branch.name !== "main";
  const hasData = volumes.some((volume) => volume.name !== ATTACHMENTS_VOLUME);
  const liveHasVolumes = spaceVolumes.some(
    (volume) =>
      volume.name !== ATTACHMENTS_VOLUME &&
      volume.branch?.name === "main" &&
      volume.branch.workflowId === branch.workflowId
  );
  const loading = result.type !== "complete" && volumes.length === 0;
  const actions =
    isDraft && hasData ? (
      <ResetBranchDataButton branchId={branch.id} disabled={!canWrite} />
    ) : undefined;

  if (volumeName && volumes.some((volume) => volume.name === volumeName)) {
    return (
      <DirectoryFiles
        directory={{
          type: "directory",
          title: volumeName,
          pathBase: spaceVolumePath(spaceId, branch.id, volumeName),
          relativePath: "",
        }}
        onOpen={onOpen}
        actions={actions}
      />
    );
  }

  return (
    <FileList
      title={volumeName ?? "Files"}
      count={loading ? undefined : volumeName ? 0 : volumes.length}
      actions={actions}
    >
      {loading ? (
        <EmptyState
          icon={<WavyLine size={16} opacity={0.4} />}
          title="Loading files…"
        />
      ) : isDraft && !hasData && liveHasVolumes ? (
        <DraftStorageEmptyState
          spaceId={spaceId}
          branchId={branch.id}
          liveHasVolumes
          disabled={!canWrite}
        />
      ) : !volumeName && volumes.length > 0 ? (
        volumes.map((volume) => (
          <FileListRow
            key={volume.name}
            name={volume.name}
            folder
            onPress={() =>
              onOpen({
                type: "directory",
                title: volume.name,
                pathBase: spaceVolumePath(spaceId, branch.id, volume.name),
                relativePath: "",
              })
            }
          />
        ))
      ) : (
        <EmptyState
          iconName="folder"
          title="No files yet"
          description={
            volumeName
              ? "Files this step reads and writes will appear here."
              : "Files this workflow reads and writes will appear here."
          }
        />
      )}
    </FileList>
  );
}

function DirectoryFiles({
  directory,
  onOpen,
  onBack,
  backLabel,
  actions,
}: {
  directory: Directory;
  onOpen: (location: Location) => void;
  onBack?: () => void;
  backLabel?: string;
  actions?: ReactNode;
}) {
  const { entries, loading, error } = useVolumeDirectory(
    directory.pathBase,
    directory.relativePath
  );

  return (
    <FileList
      title={directory.title}
      count={loading || error ? undefined : entries.length}
      onBack={onBack}
      backLabel={backLabel}
      actions={actions}
    >
      {loading ? (
        <EmptyState
          icon={<WavyLine size={16} opacity={0.4} />}
          title="Loading files…"
        />
      ) : error ? (
        <EmptyState
          iconName="traffic-cone"
          title="Couldn’t load these files"
          description={error}
        />
      ) : entries.length === 0 ? (
        <EmptyState iconName="folder" title="This folder is empty" />
      ) : (
        entries.map((entry) => {
          const relativePath = directory.relativePath
            ? `${directory.relativePath}/${entry.name}`
            : entry.name;
          const url = volumeFilePath(directory.pathBase, relativePath);
          const folder = entry.kind === "directory";
          return (
            <FileListRow
              key={entry.name}
              name={entry.name}
              folder={folder}
              sizeBytes={entry.size}
              thumbnailUrl={url}
              onPress={() =>
                onOpen(
                  folder
                    ? { ...directory, title: entry.name, relativePath }
                    : {
                        type: "file",
                        file: {
                          url,
                          fileName: entry.name,
                          sizeBytes: entry.size,
                        },
                      }
                )
              }
            />
          );
        })
      )}
    </FileList>
  );
}
